import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const BookingSection = () => {
  const mountedAt = useRef(Date.now());
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", slot: "dopoledne", website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | demo
  const [serverError, setServerError] = useState("");

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const next = {};
    if (form.name.trim().length < 2) next.name = "Napište prosím své jméno.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Zadejte prosím platnou e-mailovou adresu.";
    if (!form.date) next.date = "Vyberte prosím datum.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setStatus("sending");
    try {
      const res = await fetch(`${API}/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, elapsed_ms: Date.now() - mountedAt.current }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.status === "ok") {
        setStatus("sent");
      } else if (res.ok && data.status === "demo") {
        setStatus("demo");
      } else {
        setStatus("idle");
        setServerError(data.detail || "Návrh termínu se nepodařilo odeslat. Zkuste to prosím znovu.");
      }
    } catch {
      setStatus("idle");
      setServerError("Návrh termínu se nepodařilo odeslat. Zkontrolujte prosím připojení a zkuste to znovu.");
    }
  };

  const inputCls = (err) =>
    `bg-transparent border-b ${err ? "border-red-700" : "border-[#E5E5E5]"} focus:border-[#B69A58] outline-none py-3 px-0 w-full text-base transition-colors duration-300 rounded-none placeholder-[#A3A3A3]`;

  return (
    <section id="termin" data-testid="booking-section" className="py-16 md:py-20 border-t border-[#F0F0F0] bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Domluvit konzultaci
          </p>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.15] text-[#252525] mt-6">
            Vyberte si termín rovnou
          </h2>
          <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-5">
            Navrhněte si den a část dne, která vám vyhovuje. Ozvu se vám e-mailem
            a konkrétní čas společně potvrdíme — osobní konzultace po dohodě u vás
            v Ústí nad Labem a okolí, online po celé ČR.
          </p>

          {status === "sent" ? (
            <div role="status" data-testid="booking-success" className="flex items-center gap-4 mt-8 border border-[#F0F0F0] bg-white p-6">
              <Check className="h-6 w-6 text-[#B69A58] shrink-0" strokeWidth={1.5} aria-hidden="true" />
              <p className="text-base text-[#252525]">
                Návrh termínu je u mě. Ozvu se vám e-mailem a domluvíme přesný čas.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate data-testid="booking-form" className="mt-8 grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="booking-name" className="block text-xs uppercase tracking-widest text-[#737373] font-medium mb-1">
                  Jméno *
                </label>
                <input
                  id="booking-name"
                  data-testid="booking-name-input"
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={set("name")}
                  className={inputCls(errors.name)}
                  aria-invalid={!!errors.name}
                />
                {errors.name && <p role="alert" data-testid="booking-name-error" className="text-sm text-red-700 mt-2">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="booking-email" className="block text-xs uppercase tracking-widest text-[#737373] font-medium mb-1">
                  E-mail *
                </label>
                <input
                  id="booking-email"
                  data-testid="booking-email-input"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={set("email")}
                  className={inputCls(errors.email)}
                  aria-invalid={!!errors.email}
                />
                {errors.email && <p role="alert" data-testid="booking-email-error" className="text-sm text-red-700 mt-2">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="booking-date" className="block text-xs uppercase tracking-widest text-[#737373] font-medium mb-1">
                  Den konzultace *
                </label>
                <input
                  id="booking-date"
                  data-testid="booking-date-input"
                  type="date"
                  min={tomorrow}
                  value={form.date}
                  onChange={set("date")}
                  className={inputCls(errors.date)}
                  aria-invalid={!!errors.date}
                />
                {errors.date && <p role="alert" data-testid="booking-date-error" className="text-sm text-red-700 mt-2">{errors.date}</p>}
              </div>
              <fieldset>
                <legend className="text-xs uppercase tracking-widest text-[#737373] font-medium mb-3">
                  Část dne *
                </legend>
                <div className="flex gap-6 pt-2">
                  {[
                    ["dopoledne", "Dopoledne"],
                    ["odpoledne", "Odpoledne"],
                  ].map(([value, label]) => (
                    <label key={value} className="flex items-center gap-2 text-base text-[#252525] cursor-pointer">
                      <input
                        type="radio"
                        name="booking-slot"
                        data-testid={`booking-slot-${value}`}
                        value={value}
                        checked={form.slot === value}
                        onChange={set("slot")}
                        className="accent-[#B69A58] w-4 h-4"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
                <label htmlFor="booking-website">Webové stránky</label>
                <input
                  id="booking-website"
                  data-testid="booking-honeypot"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={set("website")}
                />
              </div>

              <div className="sm:col-span-2">
                {serverError && <p role="alert" data-testid="booking-server-error" className="text-sm text-red-700 mb-4">{serverError}</p>}
                {status === "demo" && (
                  <p role="status" data-testid="booking-demo-notice" className="text-sm text-[#737373] border border-[#E5E5E5] bg-white p-4 mb-4">
                    Demonstrační režim: návrh byl uložen, ale e-mailové doručování zatím není nastavené.
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="booking-submit-button"
                  disabled={status === "sending"}
                  className="border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-sm uppercase tracking-widest font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? "Odesílám…" : "Navrhnout termín"}
                </button>
                <p className="text-xs text-[#737373] leading-relaxed mt-4">
                  Termín je nezávazný návrh — přesný čas potvrdím e-mailem.{" "}
                  <Link to="/ochrana-osobnich-udaju" data-testid="booking-privacy-link" className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300">
                    Informace o zpracování osobních údajů
                  </Link>
                </p>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
